import { useState, useEffect, useContext } from 'react';
import Fuse from 'fuse.js';
import { InsurancePlan } from '../../utils/types';
import { DataContext } from '../../containers/InsuranceChecker';

interface SearchMatch {
    plan: string;
    score: number;
}

// Combines results from 2 lists, removing duplicates and leaving the match with
// the highest score in the result.
const combineResults = (list1: SearchMatch[], list2: SearchMatch[]): SearchMatch[] => {
    const found = new Set<string>();
    const result: { plan: string, score: number }[] = [];
    const combined = [ ...list1, ...list2 ].sort((a, b) => b.score - a.score);
    for (const item of combined) {
        if (!found.has(item.plan)) {
            found.add(item.plan);
            result.push(item);
        }
    }
    return result.sort((a, b) => b.score - a.score);
};

export const useSearch = () => {
    const { plans } = useContext(DataContext);

    // Using fuse for fuzzy search. https://www.fusejs.io/
    const [ fuse, setFuse ] = useState<Fuse<InsurancePlan>>();

    // From fuse.js documentation:
    // A threshold of 0.0 requires a perfect match (of both
    // letters and location), a threshold of 1.0 would match anything.
    const MATCH_THRESHOLD = 0.34; // this seemed reasonable based on my usage.
    useEffect(() => {
        // Initialize Fuse search library.
        const f = new Fuse(plans, {
            keys: [ 'name' ],
            threshold: MATCH_THRESHOLD,
            includeScore: true,
        });
        setFuse(f);
    }, []);

    // This is a function that finds different kinds of partial matches
    // between the search term and the plan name that sits on top of
    // the fuse library.
    const findPartialMatches = (term: string): SearchMatch[] => {
        // Break out the whole search term into individual terms.
        const terms = term.split(' ').filter(t => t.length > 0);

        // Get the first term - if there is a match on the first term
        // with the beginning of a plan name this should have a high score.
        const firstTerm = terms[0] ?? null;

        // Combine the user's first two terms if relevant (e.g. "Blue Cross"
        // should be "BlueCross") and if there is an exact match of the beginning
        // of the plan name this should also have a high score.
        const combinedFirstTerm = terms[0] && terms[1] ? terms[0] + terms[1] : null;

        // Go through the plans and see if there is an exact match on the first
        // term or combinedFirstTerm and if there is, add it to the results with a high
        // score. Also, if the plan name has at least 2 words, combine the first two
        // terms of the plan name and make the same comparison.
        const results: SearchMatch[] = [];
        for (const plan of plans) {
            const planWords = plan.name.split(' ');
            if (planWords.length >= 2) {
                const planCombinedFirstTerm = planWords[0] + planWords[1];
                // E.g. Search Term: "Blue Cross Blue Shield" and plan name "Blue Cross Something Else" -> match.
                if (combinedFirstTerm && planCombinedFirstTerm.toLowerCase().startsWith(combinedFirstTerm.toLowerCase())) {
                    results.push({ plan: plan.name, score: 1 });
                    continue;
                }
                // E.g. Search Term: "HealthNet ..." and plan name: "Health Net" -> match.
                if (firstTerm && planCombinedFirstTerm.toLowerCase().startsWith(firstTerm.toLowerCase())) {
                    results.push({ plan: plan.name, score: 0.5 });
                }
            }
            // E.g. Search Term: "Blue Cross" and plan name "BlueCross" -> match.
            if (combinedFirstTerm && plan.name.toLowerCase().startsWith(combinedFirstTerm.toLowerCase())) {
                results.push({ plan: plan.name, score: 1 });
                continue;
            }
            // E.g. Search Term: "Anthem priority Select" and plan name "Anthem Blue cross" -> match.
            if (firstTerm && plan.name.toLowerCase().startsWith(firstTerm.toLowerCase())) {
                results.push({ plan: plan.name, score: 0.5 });
                continue;
            }
            // Finally, if any word of the plan name starts with one of the terms, push it to the results with
            // a pretty low score. (e.g. Search Term: "UHC Gold PPO" and plan name "All Savers - UHC" -> match).
            for (const term of terms) {
                const planWords = plan.name.split(' ');
                for (const word of planWords) {
                    if (word.toLowerCase().startsWith(term.toLowerCase())) {
                        results.push({ plan: plan.name, score: 0.1 });
                        break;
                    }
                }
            }
        }

        // Sort the results by score, highest score first.
        const sorted = results.sort((a, b) => a.score - b.score);
        return sorted;
    };

    const handleSearch = (value: string): string[] => {
        // Show all plans by default (retains current behavior).
        if (value === '') {
            const allPlans = plans.map(p => p.name);
            return allPlans;
        }
        let results: SearchMatch[] = [];
        // Use fuse search if it's defined.
        if (fuse) {
            results = fuse.search(value).map(p => ({ plan: p.item.name, score: p.score ?? 0 }));
        } else {
            // Fallback search (seems unlikely this would ever happen,
            // but just in case use basic string matching).
            results = plans.filter(p => {
                if (p.name.toLowerCase().includes(value.toLowerCase())) {
                    return true;
                }
                return false;
            }).map(p => ({ plan: p.name, score: 1 }));
        }
        const partialMatches = findPartialMatches(value);
        results = combineResults(results, partialMatches);
        return results.map(r => r.plan);
    };

    return { handleSearch };
};
